<template>
  <Modal :title="modalTitle" :error="error" @close="$emit('close')">
    <div v-if="loading" class="muted">Loading…</div>
    <div v-else-if="!email" class="muted">Email not found.</div>
    <div v-else class="emailShell">
      <div class="addressBlock">
        <div class="fieldRow">
          <div class="fieldKey">From</div>
          <div class="fieldValue">{{ fromDisplay }}</div>
        </div>

        <div class="fieldRow">
          <div class="fieldKey">To</div>
          <div class="fieldValue">{{ toDisplay }}</div>
        </div>

        <div class="fieldRow">
          <div class="fieldKey">Direction</div>
          <div class="fieldValue">
            <span class="directionBadge" :class="directionClass">{{ directionLabel }}</span>
          </div>
        </div>
      </div>

      <div class="messageBlock">
        <div class="subjectLine">{{ email.clean_subject || email.subject || '(no subject)' }}</div>
        <div class="bodyLabel">Body</div>
        <div class="emailBody">{{ bodyDisplay }}</div>
      </div>
    </div>
  </Modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import Modal from './Modal.vue'
import { apiFetch } from '../services/api.ts'

const props = defineProps({
  accountId: { type: [String, Number], required: true },
  emailId: { type: [String, Number], required: true },
  initialEmail: { type: Object, default: null },
})

defineEmits(['close'])

const loading = ref(false)
const error = ref('')
const email = ref(props.initialEmail || null)

const modalTitle = computed(() => String(email.value?.clean_subject || email.value?.subject || 'Email'))
const fromDisplay = computed(() => String(email.value?.from || email.value?.owner_name || email.value?.owner_email || email.value?.who_name || email.value?.who_email || '').trim() || '—')
const toDisplay = computed(() => String(email.value?.to || email.value?.who_name || email.value?.who_email || email.value?.owner_name || email.value?.owner_email || '').trim() || '—')
const bodyDisplay = computed(() => String(email.value?.body || email.value?.preview || email.value?.description || '').trim())

const directionRaw = computed(() => String(email.value?.direction || '').trim().toLowerCase())
const directionLabel = computed(() => {
  if (directionRaw.value === 'inbound' || directionRaw.value === 'in') return 'Received'
  if (directionRaw.value === 'outbound' || directionRaw.value === 'out') return 'Sent'
  return email.value?.direction || '—'
})
const directionClass = computed(() => {
  if (directionRaw.value === 'inbound' || directionRaw.value === 'in') return 'in'
  if (directionRaw.value === 'outbound' || directionRaw.value === 'out') return 'out'
  return ''
})

async function loadEmail() {
  const accountId = String(props.accountId || '').trim()
  const emailId = String(props.emailId || '').trim()
  if (!accountId || !emailId) return
  if (props.initialEmail && String(props.initialEmail?.id || '').trim() === emailId) {
    email.value = props.initialEmail
    return
  }
  loading.value = true
  error.value = ''
  try {
    const resp = await apiFetch(`/accounts/${encodeURIComponent(accountId)}/salesforce_emails/${encodeURIComponent(emailId)}`)
    email.value = resp?.email || null
  } catch (e) {
    error.value = e?.message || 'Failed to load email'
    email.value = null
  } finally {
    loading.value = false
  }
}

watch(() => [props.accountId, props.emailId], () => {
  void loadEmail()
}, { immediate: true })
</script>

<style scoped>
.emailShell {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.addressBlock,
.messageBlock {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
  min-width: 0;
}

.fieldRow {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
}

.fieldKey,
.bodyLabel {
  font-size: 12px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.fieldValue {
  min-width: 0;
  color: var(--text);
  font-weight: 600;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}

.subjectLine {
  color: var(--text);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
}

.emailBody {
  color: var(--text);
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 80px;
  font-size: 14px;
}

.directionBadge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 999px;
  line-height: 1.4;
  display: inline-block;
}

.directionBadge.in {
  background: #dbeafe;
  color: #1d4ed8;
}

.directionBadge.out {
  background: #dcfce7;
  color: #15803d;
}
</style>
